import { API_URL as BASE_URL } from '../../lib/config';
const API_URL = `${BASE_URL}/platform-admin`;

export interface PlatformAdmin {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'SUPER_ADMIN' | 'BILLING_ADMIN' | 'SUPPORT' | 'DEVELOPER';
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAdminUserDto {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role: 'SUPER_ADMIN' | 'BILLING_ADMIN' | 'SUPPORT' | 'DEVELOPER';
}

export interface UpdateAdminUserDto {
  firstName?: string;
  lastName?: string;
  role?: 'SUPER_ADMIN' | 'BILLING_ADMIN' | 'SUPPORT' | 'DEVELOPER';
  isActive?: boolean;
}

export interface ChangePasswordDto {
  currentPassword: string;
  newPassword: string;
}

export interface ResetPasswordDto {
  newPassword: string;
}

export interface AdminStats {
  total: number;
  active: number;
  byRole: Array<{ role: string; count: number }>;
}

export const adminUsersService = {
  async getAllAdmins(token: string): Promise<PlatformAdmin[]> {
    const response = await fetch(`${API_URL}/admin-users`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch admin users');
    }

    return response.json();
  },

  async getAdminById(token: string, id: string): Promise<PlatformAdmin> {
    const response = await fetch(`${API_URL}/admin-users/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch admin user');
    }

    return response.json();
  },

  async createAdmin(
    token: string,
    data: CreateAdminUserDto,
  ): Promise<PlatformAdmin> {
    const response = await fetch(`${API_URL}/admin-users`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create admin user');
    }

    return response.json();
  },

  async updateAdmin(
    token: string,
    id: string,
    data: UpdateAdminUserDto,
  ): Promise<PlatformAdmin> {
    const response = await fetch(`${API_URL}/admin-users/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update admin user');
    }

    return response.json();
  },

  async deleteAdmin(
    token: string,
    id: string,
  ): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${API_URL}/admin-users/${id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to delete admin user');
    }

    return response.json();
  },

  async changePassword(
    token: string,
    data: ChangePasswordDto,
  ): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${API_URL}/admin-users/change-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to change password');
    }

    return response.json();
  },

  async resetPassword(
    token: string,
    id: string,
    data: ResetPasswordDto,
  ): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${API_URL}/admin-users/${id}/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to reset password');
    }

    return response.json();
  },

  async getStats(token: string): Promise<AdminStats> {
    const response = await fetch(`${API_URL}/admin-users/stats`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch admin stats');
    }

    return response.json();
  },
};
